/** Mapping editor for one external product/option ID: lists the
 *  Additional_Ext_ID rows carrying that value, allows create / flag toggles /
 *  delete. The tour's own custom_id matches are shown read-only (changing a
 *  tour's primary external ID has import-matching side effects). */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import * as syncApi from '@/api/sync';
import { EXT_ID_TYPES, type ProductMapping, type TourOption } from '@/api/sync';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';

interface Props {
  open: boolean;
  onClose: () => void;
  tourOperatorId: number;
  integration: string;
  account: string | null;
  /** The external value being mapped (product/option/variation ID). */
  extValue: string;
  /** Cross-reference rows for this value from the products response. */
  mappings: ProductMapping[];
  /** Called after any mapping change so the parent can refresh cross-refs. */
  onChanged: () => void;
}

export function MappingDrawer({
  open,
  onClose,
  tourOperatorId,
  integration,
  account,
  extValue,
  mappings,
  onChanged,
}: Props) {
  const { show } = useSnackbar();
  const queryClient = useQueryClient();

  const [tour, setTour] = useState<TourOption | null>(null);
  const [idType, setIdType] = useState('product_id');
  const [apiQueryable, setApiQueryable] = useState(true);
  const [useForMatching, setUseForMatching] = useState(true);
  const [deletePk, setDeletePk] = useState<number | null>(null);

  const rows = useQuery({
    queryKey: ['ext-ids', tourOperatorId, integration, extValue],
    queryFn: () => syncApi.listExtIds(tourOperatorId, { integration, search: extValue }),
    enabled: open,
  });
  const exactRows = (rows.data?.results ?? []).filter((r) => r.custom_id === extValue);

  const afterChange = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ext-ids', tourOperatorId] });
    onChanged();
  };

  const createMut = useMutation({
    mutationFn: () =>
      syncApi.createExtId(tourOperatorId, {
        tour_id: tour!.id,
        custom_id: extValue,
        id_type: idType,
        integration,
        restech_account: account,
        api_queryable: apiQueryable,
        use_for_matching: useForMatching,
      }),
    onSuccess: async () => {
      show('Mapping created.', 'success');
      setTour(null);
      await afterChange();
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const toggleMut = useMutation({
    mutationFn: (args: { pk: number; body: syncApi.ExtIdWriteInput }) =>
      syncApi.updateExtId(tourOperatorId, args.pk, args.body),
    onSuccess: afterChange,
    onError: (e) => show((e as Error).message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: (pk: number) => syncApi.deleteExtId(tourOperatorId, pk),
    onSuccess: async () => {
      show('Mapping removed.', 'info');
      await afterChange();
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const customIdMatches = mappings.filter((m) => m.via === 'custom_id');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{ sx: { width: 420, p: 2 } }}
    >
      <Typography variant="h6" gutterBottom>
        Mappings for <code>{extValue}</code>
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {integration}
        {account ? ` / ${account}` : ''}
      </Typography>

      {customIdMatches.length > 0 && (
        <Alert severity="info" sx={{ my: 1 }}>
          Primary match via tour custom_id:{' '}
          {customIdMatches.map((m) => m.tour.name ?? `#${m.tour.id}`).join(', ')} (read-only).
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Mapping rows
      </Typography>
      <Stack spacing={1} sx={{ my: 1 }}>
        {exactRows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No Additional_Ext_ID rows for this value.
          </Typography>
        )}
        {exactRows.map((row) => (
          <Box key={row.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                → {row.tour.name ?? `Tour #${row.tour.id}`}
              </Typography>
              <Chip size="small" variant="outlined" label={row.id_type_display} />
              <IconButton size="small" color="error" onClick={() => setDeletePk(row.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={row.api_queryable}
                    onChange={(e) =>
                      toggleMut.mutate({ pk: row.id, body: { api_queryable: e.target.checked } })
                    }
                  />
                }
                label={<Typography variant="caption">API queryable</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={row.use_for_matching}
                    onChange={(e) =>
                      toggleMut.mutate({
                        pk: row.id,
                        body: { use_for_matching: e.target.checked },
                      })
                    }
                  />
                }
                label={<Typography variant="caption">Use for matching</Typography>}
              />
            </Box>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Add mapping
      </Typography>
      <Stack spacing={1.5}>
        <RemoteAutocomplete<TourOption>
          label="Tour"
          placeholder="Search by name, ID or custom_id"
          size="small"
          fetchOnEmpty
          queryFn={(q, signal) => syncApi.searchTours(tourOperatorId, q, signal)}
          getOptionLabel={(t) => `${t.name} [#${t.id}${t.custom_id ? ` · ${t.custom_id}` : ''}]`}
          getOptionKey={(t) => t.id}
          value={tour}
          onChange={(v) => setTour(v as TourOption | null)}
        />
        <TextField
          select
          size="small"
          label="ID type"
          value={idType}
          onChange={(e) => setIdType(e.target.value)}
        >
          {EXT_ID_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={apiQueryable}
                onChange={(e) => setApiQueryable(e.target.checked)}
              />
            }
            label={<Typography variant="caption">API queryable</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={useForMatching}
                onChange={(e) => setUseForMatching(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Use for matching</Typography>}
          />
        </Box>
        <Button
          variant="contained"
          disabled={!tour || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Create mapping
        </Button>
      </Stack>

      <ConfirmDialog
        open={deletePk !== null}
        title="Remove mapping?"
        message={`The mapping of "${extValue}" will be deleted. Bookings for this external ID may stop matching a tour.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deletePk !== null) deleteMut.mutate(deletePk);
          setDeletePk(null);
        }}
        onCancel={() => setDeletePk(null)}
      />
    </Drawer>
  );
}
